import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../integrations/client.ts';
import { User, Session } from '@supabase/supabase-js';

interface Profile {
  name: string;
  subscription_type: string;
  theme_preference: string;
  vocabulary_level?: string;
  course_length?: number;
}

interface UserData {
  id: string;
  email: string;
  name: string;
  username: string;
  subscription_type: string;
  subscriptionType: string;
  subscriptionStatus: string;
  themePreference: string;
  vocabularyLevel?: string;
  vocabulary_level?: string;
  courseLength?: number;
  course_length?: number;
  emailVerified: boolean;
}

interface AuthContextType {
  user: UserData | null;
  session: Session | null;
  isAuthenticated: boolean;
  loading: boolean;
  profileLoaded: boolean;
  error: string | null;
  refreshProfile: () => Promise<void>;
  isEmailVerified: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  const profileFetchInProgressRef = useRef(false);
  const lastProfileFetchRef = useRef<number>(0);
  const userRef = useRef<UserData | null>(null);

  // Keep userRef in sync with user state
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Fetch user profile - no debouncing on critical auth flows
  const fetchUserProfile = useCallback(async (userId: string, skipDebounce = false, retryCount = 0): Promise<Profile | null> => {
    const now = Date.now();
    const MAX_RETRIES = 3;
    const BASE_DELAY = 1000; // 1 second

    // Debounce: Don't fetch if we fetched within the last 2 seconds (unless skipDebounce is true or retrying)
    // IMPORTANT: On initial mount (skipDebounce=true), always fetch to ensure profile loads
    if (!skipDebounce && retryCount === 0 && now - lastProfileFetchRef.current < 2000) {
      console.log('Profile fetch debounced - too soon since last fetch');
      return null;
    }

    // Prevent concurrent fetches - but wait if one is in progress and this is critical
    if (profileFetchInProgressRef.current) {
      if (skipDebounce) {
        // Critical fetch (initial load) - wait for in-progress fetch to complete
        console.log('Waiting for in-progress profile fetch to complete...');
        await new Promise(resolve => setTimeout(resolve, 500));
        // Try again after waiting
        if (!profileFetchInProgressRef.current) {
          return fetchUserProfile(userId, true, retryCount);
        }
      }
      return null;
    }

    try {
      profileFetchInProgressRef.current = true;
      lastProfileFetchRef.current = now;

      const { data, error } = await supabase
        .from('profiles')
        .select('name, subscription_type, theme_preference, vocabulary_level, course_length')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        // If we get a 401 or permission error and haven't exceeded retries, try again
        const isAuthError = error.message.includes('401') ||
                           error.message.includes('permission') ||
                           error.message.includes('JWT') ||
                           error.code === 'PGRST301';

        if (isAuthError && retryCount < MAX_RETRIES) {
          // Release the lock before retrying
          profileFetchInProgressRef.current = false;

          // Exponential backoff: 1s, 2s, 4s
          const delay = BASE_DELAY * Math.pow(2, retryCount);
          console.log(`Profile fetch failed with auth error (attempt ${retryCount + 1}/${MAX_RETRIES}), retrying in ${delay}ms...`);

          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchUserProfile(userId, true, retryCount + 1); // skipDebounce=true for retries
        }

        setError(`Failed to fetch user profile: ${error.message}`);
        return null;
      }

      if (!data) {
        // Try to create the profile if it doesn't exist
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          const user = sessionData?.session?.user;

          if (user) {
            const { data: newProfile, error: createError } = await supabase
              .from('profiles')
              .insert({
                id: userId,
                name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
                subscription_type: user.user_metadata?.subscription_type || 'free',
                theme_preference: user.user_metadata?.theme_preference || 'light',
                vocabulary_level: 'auto',
                course_length: 0
              })
              .select('name, subscription_type, theme_preference, vocabulary_level, course_length')
              .single();

            if (createError) {
              setError(`Failed to create user profile: ${createError.message}`);
              return null;
            }

            return newProfile;
          }
        } catch (createErr: any) {
          setError(`Exception creating profile: ${createErr.message}`);
        }

        return null;
      }

      return data;
    } catch (err: any) {
      setError(`Exception fetching profile: ${err.message}`);
      return null;
    } finally {
      profileFetchInProgressRef.current = false;
    }
  }, []);

  // Build user data object from session and profile
  const buildUserData = useCallback((sessionUser: User, profile: Profile | null): UserData => {
    const userData = {
      id: sessionUser.id,
      email: sessionUser.email || '',
      name: profile?.name || sessionUser.user_metadata?.name || sessionUser.email?.split('@')[0] || 'User',
      username: sessionUser.email || '',
      subscription_type: profile?.subscription_type || 'free',
      subscriptionType: profile?.subscription_type || 'free',
      subscriptionStatus: profile?.subscription_type || 'free',
      themePreference: profile?.theme_preference || sessionUser.user_metadata?.theme_preference || 'light',
      vocabularyLevel: profile?.vocabulary_level || 'auto',
      vocabulary_level: profile?.vocabulary_level || 'auto',
      courseLength: profile?.course_length || 0,
      course_length: profile?.course_length || 0,
      emailVerified: !!sessionUser.email_confirmed_at
    };



    return userData;
  }, []);

  // Public method to refresh profile data
  const refreshProfile = useCallback(async () => {
    if (!session?.user) {

      return;
    }


    // Skip debounce for manual refresh
    const profile = await fetchUserProfile(session.user.id, true);

    if (!mountedRef.current) return;

    // Only update if we successfully fetched a profile
    if (profile !== null) {
      const userData = buildUserData(session.user, profile);
      setUser(userData);

    } else {

    }
  }, [session, fetchUserProfile, buildUserData]);

  // Setup auth state listener
  useEffect(() => {
    mountedRef.current = true;
    let authSubscription: any = null;
    let safetyTimeout: NodeJS.Timeout | undefined;
    let sessionRecoveryTimeout: NodeJS.Timeout | undefined;

    // Async initialization function
    const initialize = async () => {
      try {
        // CRITICAL FIX: Explicitly check for existing session on mount
        // This ensures we load persisted sessions from localStorage immediately
        const { data: { session: existingSession } } = await supabase.auth.getSession();

        if (existingSession?.user && mountedRef.current) {
          // We have a persisted session - load it immediately
          const profile = await fetchUserProfile(existingSession.user.id, true);

          if (mountedRef.current) {
            const fullUserData = buildUserData(existingSession.user, profile);
            setSession(existingSession);
            setUser(fullUserData);
            setError(null);
            // Mark profile as loaded (we attempted to fetch it, even if null)
            // This prevents infinite loading while allowing default 'free' fallback
            setProfileLoaded(true);
            setLoading(false);
          }
        }

        // Set up listener for ongoing session changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
          if (!mountedRef.current) return;

          if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
            if (currentSession?.user) {
              // Don't reload on sign-in - just update the auth state
              // The App.js navigation logic will handle redirects based on auth state
              // This prevents issues with email verification flow and other tabs auto-signing in

              // Keep loading=true until everything is ready to prevent flashing
              // Fetch profile data
              const profile = await fetchUserProfile(currentSession.user.id, true);

              if (!mountedRef.current) return;

              // Build user data with profile information (profile may be null during callback)
              const fullUserData = buildUserData(currentSession.user, profile);

              // Clear all timeouts since auth completed successfully
              if (safetyTimeout) {
                clearTimeout(safetyTimeout);
              }
              if (sessionRecoveryTimeout) {
                clearTimeout(sessionRecoveryTimeout);
              }

              // Set all state at once to minimize re-renders
              setSession(currentSession);
              setUser(fullUserData);
              setError(null);
              // Mark profile as loaded (we attempted to fetch it)
              setProfileLoaded(true);
              setLoading(false);
            } else {
              // Clear all timeouts for no-session case
              if (safetyTimeout) {
                clearTimeout(safetyTimeout);
              }
              if (sessionRecoveryTimeout) {
                clearTimeout(sessionRecoveryTimeout);
              }
              setSession(null);
              setUser(null);
              setProfileLoaded(false);
              setLoading(false);
            }
          } else if (event === 'SIGNED_OUT') {
            // Clear all timeouts on sign out
            if (safetyTimeout) {
              clearTimeout(safetyTimeout);
            }
            if (sessionRecoveryTimeout) {
              clearTimeout(sessionRecoveryTimeout);
            }
            setSession(null);
            setUser(null);
            setProfileLoaded(false);
            setError(null);
            setLoading(false);
          } else if (event === 'TOKEN_REFRESHED' && currentSession) {
            setSession(currentSession);

            // Periodically refresh profile on token refresh to catch subscription changes
            // Only if it's been more than 5 minutes since last profile fetch
            const timeSinceLastFetch = Date.now() - lastProfileFetchRef.current;
            if (timeSinceLastFetch > 300000 && currentSession?.user) { // 5 minutes
              const profile = await fetchUserProfile(currentSession.user.id, true);

              if (!mountedRef.current) return;

              if (profile !== null) {
                const userData = buildUserData(currentSession.user, profile);
                setUser(userData);
              }
            }
          } else if (event === 'USER_UPDATED' && currentSession?.user) {
            // Fetch updated profile - skip debounce to ensure we get fresh data
            const profile = await fetchUserProfile(currentSession.user.id, true);

            if (!mountedRef.current) return;

            // Always update user on USER_UPDATED
            const userData = buildUserData(currentSession.user, profile);
            setUser(userData);

            setSession(currentSession);
          }
        });

        authSubscription = subscription;

        // Session recovery - if INITIAL_SESSION event doesn't fire, explicitly check again
        sessionRecoveryTimeout = setTimeout(async () => {
          if (mountedRef.current && loading) {
            const { data: { session: recoveredSession } } = await supabase.auth.getSession();

            if (recoveredSession?.user && mountedRef.current) {
              const profile = await fetchUserProfile(recoveredSession.user.id, true);

              if (mountedRef.current) {
                const fullUserData = buildUserData(recoveredSession.user, profile);
                setSession(recoveredSession);
                setUser(fullUserData);
                setError(null);
                // Mark profile as loaded (we attempted to fetch it)
                setProfileLoaded(true);
                setLoading(false);
              }
            }
          }
        }, 1000);

        // Safety timeout - ensure loading ends even if nothing happens
        safetyTimeout = setTimeout(() => {
          if (mountedRef.current) {

            setLoading(false);
          }
        }, 2000);

      } catch (err: any) {
        
        setError(err.message);
        setLoading(false);
      }
    };

    initialize();

    // Cleanup
    return () => {
      mountedRef.current = false;
      if (safetyTimeout) {
        clearTimeout(safetyTimeout);
      }
      if (sessionRecoveryTimeout) {
        clearTimeout(sessionRecoveryTimeout);
      }
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
    };
  }, [fetchUserProfile, buildUserData]);

  const isEmailVerified = useCallback(() => {
    return user?.emailVerified || false;
  }, [user]);

  const value: AuthContextType = {
    user,
    session,
    isAuthenticated: !!user && !!session,
    loading,
    profileLoaded,
    error,
    refreshProfile,
    isEmailVerified
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
